import {
  Bars3Icon,
  NumberedListIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
} from '@superfunnel/ui-core/v2/Drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Computer, Flask, GraphUp, Settings, UserScan } from 'iconoir-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import sparksNew from '../../assets/svgs/icons/sparks-new.svg';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import { UserContext } from '../../contexts/UserContext';
import { classNames } from '../../utils';
import DashboardSidebar, {
  SidebarItem,
  SidebarTeamItem,
} from './DashboardSidebar';
import { MaintenanceOverlay } from './MaintenanceOverlay';
import { NoticeBanner } from './NoticeBanner';
import { VersionUpdateModal } from './VersionUpdateModal';

export default function DashboardLayout() {
  const { slug } = useParams();
  const location = useLocation();
  const { track } = useAnalytics();
  const { firebaseUser, logout, isSupportAdmin } = useContext(AuthContext);
  const { accounts } = useContext(UserContext);
  const { systemConfig, getScreenMaintenanceConfig } = useSystemConfig();
  // const hasFacebookAdsFeature = useHasFeature('facebookAds');

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  // Check for global maintenance (skip for support admins)
  const globalMaintenanceConfig = systemConfig?.globalMaintenance;
  const isGlobalMaintenance =
    globalMaintenanceConfig?.enabled && !isSupportAdmin;

  // Check for notice banner
  const noticeBannerConfig = systemConfig?.noticeBanner;
  const showNoticeBanner = noticeBannerConfig?.enabled;

  // Check for screen-level maintenance
  // Extract screen path from URL: /workspace/{slug}/{screen}/...
  const currentScreen = useMemo(() => {
    const pathParts = location.pathname.split('/');
    // Path format: /workspace/{slug}/{screen}
    const workspaceIndex = pathParts.indexOf('workspace');
    if (workspaceIndex !== -1 && pathParts.length > workspaceIndex + 2) {
      return pathParts[workspaceIndex + 2];
    }
    return null;
  }, [location.pathname]);

  const screenMaintenanceConfig = currentScreen
    ? getScreenMaintenanceConfig(currentScreen)
    : undefined;

  // Track page views when location changes
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const section = pathParts[pathParts.length - 1] || 'overview';

    track('dashboard_page_viewed', {
      path: location.pathname,
      section: section,
      workspace_slug: slug,
    });
  }, [location.pathname, slug, track]);

  const navigation: SidebarItem[] = useMemo(() => {
    const base = `/workspace/${slug}/`;
    const items: Omit<SidebarItem, 'current'>[] = [
      {
        name: 'Create Funnel',
        href: base + 'assistant',
        icon: <img src={sparksNew} />,
        special: true,
      },
      {
        name: 'Overview',
        href: base + 'overview',
        icon: <GraphUp />,
      },
      // {
      //   name: 'Search Campaigns',
      //   href: base + 'search-campaigns',
      //   icon: InputSearch,
      // },
      // {
      //   name: 'Social Campaigns',
      //   href: base + 'social-campaigns',
      //   icon: ShareAndroid,
      // },
      { name: 'Dynamic Pages', href: base + 'pages', icon: <Computer /> },
      {
        name: 'Quiz Funnels',
        href: base + 'quiz-funnels',
        icon: <NumberedListIcon />,
      },
      {
        name: 'Experiments Hub',
        href: base + 'experiments',
        icon: <Flask />,
      },
      {
        name: 'Leads',
        href: base + 'leads',
        icon: <UserScan />,
      },
      { name: 'Settings', href: base + 'settings', icon: <Settings /> },
    ];

    return items.map((item) => ({
      ...item,
      current:
        location.pathname === item.href ||
        location.pathname.startsWith(item.href + '/'),
    }));
  }, [location, slug]);

  const teams: SidebarTeamItem[] = useMemo(() => {
    const allTeams =
      accounts?.map((account) => {
        const accountData = account.data();
        return {
          id: accountData.slug,
          name: accountData.name,
          href: `/workspace/${accountData.slug}/overview`,
          initial: accountData.name?.[0] || '',
          current: slug === accountData.slug,
        };
      }) ?? [];

    // Sort teams to put current workspace first
    const currentTeam = allTeams.find((team) => team.current);
    const otherTeams = allTeams.filter((team) => !team.current);

    return currentTeam ? [currentTeam, ...otherTeams] : allTeams;
  }, [slug, accounts]);

  return (
    <div className="flex h-screen flex-col">
      {/* Notice Banner */}
      {showNoticeBanner && noticeBannerConfig && (
        <NoticeBanner config={noticeBannerConfig} />
      )}

      {/* Global Maintenance Overlay */}
      {isGlobalMaintenance && globalMaintenanceConfig && (
        <MaintenanceOverlay
          config={globalMaintenanceConfig}
          title="System Maintenance"
        />
      )}

      {/* Version Update Modal */}
      {systemConfig?.requiredVersion && (
        <VersionUpdateModal config={systemConfig.requiredVersion} />
      )}

      <Drawer
        open={mobileSidebarOpen}
        onOpenChange={setMobileSidebarOpen}
        direction="left"
      >
        <DrawerContent
          innerClassName="p-0 overflow-hidden"
          className={showNoticeBanner ? 'top-10!' : undefined}
        >
          <div className="relative flex h-full w-full flex-col">
            <DrawerClose asChild>
              <button
                type="button"
                className="absolute top-4 right-4 z-10 -m-2.5 p-2.5"
                onClick={() => {
                  track('mobile_sidebar_closed');
                }}
              >
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </DrawerClose>
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <DashboardSidebar
              teams={teams}
              navigation={navigation}
              collapsed={desktopSidebarCollapsed}
              onCollapseChange={(collapsed) => {
                track('sidebar_toggled', {
                  collapsed: collapsed,
                  device: 'mobile',
                });
                setDesktopSidebarCollapsed(collapsed);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      {/* Static sidebar for desktop */}
      <div
        className={classNames(
          'hidden transform overflow-x-hidden transition-all duration-300 ease-in-out lg:fixed lg:bottom-0 lg:z-10 lg:flex lg:flex-col',
          showNoticeBanner ? 'lg:top-10' : 'lg:top-0',
          desktopSidebarCollapsed ? 'lg:w-[77px]' : 'lg:w-66',
        )}
      >
        <DashboardSidebar
          teams={teams}
          navigation={navigation}
          collapsed={desktopSidebarCollapsed}
          onCollapseChange={(collapsed) => {
            track('sidebar_toggled', {
              collapsed: collapsed,
              device: 'desktop',
            });
            setDesktopSidebarCollapsed(collapsed);
          }}
        />
      </div>

      {/* Sticky sidebar for mobile */}
      <div className="flex h-16 items-center gap-x-6 bg-indigo-600 px-4 py-4 shadow-xs sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-indigo-200 lg:hidden"
          onClick={() => {
            track('mobile_sidebar_opened');
            setMobileSidebarOpen(true);
          }}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="flex-1 text-sm leading-6 font-semibold text-white">
          {/* Dashboard */}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="cursor-pointer">
              <img
                className="h-8 w-8 rounded-full bg-indigo-700"
                src={
                  firebaseUser?.photoURL ??
                  'https://firebasestorage.googleapis.com/v0/b/ai-marketing-agents-406e0.firebasestorage.app/o/images%2Favatar.png?alt=media'
                }
                alt=""
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                track('logout_clicked', {
                  from: 'mobile_header',
                });
                logout();
              }}
            >
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div
        className={classNames(
          'group flex flex-1 justify-center overflow-scroll lg:h-screen lg:overflow-auto',
          desktopSidebarCollapsed ? 'lg:pl-22' : 'active lg:pl-66',
        )}
      >
        {/* <CollapseButton
          open={desktopSidebarCollapsed}
          setOpen={setDesktopSidebarCollapsed}
        /> */}
        <main className="relative h-full w-full">
          <Outlet />

          {/* Screen-level Maintenance Overlay */}
          {screenMaintenanceConfig && (
            <MaintenanceOverlay
              config={screenMaintenanceConfig}
              title="Page Under Maintenance"
              coverContent
            />
          )}
        </main>
      </div>
    </div>
  );
}

// const CollapseButton = ({
//   open,
//   setOpen,
// }: {
//   open: boolean;
//   setOpen: (open: boolean) => void;
// }) => {
//   const [hover, setHover] = useState(false);
//   return (
//     <div
//       className="absolute top-1/2 hidden h-6 w-4 -translate-y-1/2 transform cursor-pointer items-center justify-center transition-all duration-300 lg:flex"
//       style={{ left: open ? '64px' : '240px' }}
//       onMouseEnter={() => setHover(true)}
//       onMouseLeave={() => setHover(false)}
//     >
//       <Tooltip>
//         <TooltipTrigger asChild>
//           <div>
//             {hover ? (
//               <div
//                 className="flex h-6 w-4 items-center justify-center"
//                 onClick={() => setOpen(!open)}
//               >
//                 {open ? (
//                   <ChevronRightIcon className="h-6 w-6 stroke-4 text-black opacity-50 [&>path]:stroke-3" />
//                 ) : (
//                   <ChevronLeftIcon className="h-6 w-6 stroke-4 text-black opacity-50 [&>path]:stroke-3" />
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex h-6 w-4 items-center justify-center"
//                 onClick={() => setOpen(!open)}
//               >
//                 <div className="h-4 w-[2px] bg-gray-500"></div>
//               </div>
//             )}
//           </div>
//         </TooltipTrigger>
//         <TooltipContent side="right">
//           <p>{open ? 'Expand' : 'Collapse'}</p>
//         </TooltipContent>
//       </Tooltip>
//     </div>
//   );
// };
